import { useCallback, useRef } from 'react';
import { useWebSocket } from './useWebSocket';
import { useDiscord } from '../context/DiscordContext';
import { WSMessage, GameAction, MoveMessage } from '../types/game';
import { UseGameStateReturn } from './useGameState';
import { seedFromRoomId } from '../utils';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'playing';

export interface UseConnectionOptions {
  roomId: string;
  enabled: boolean;
  gameStateHook: UseGameStateReturn;
  onGameStart: () => void;
  onError: (error: string) => void;
  onCombineAnimation: (cardIds: [string, string]) => void;
  onCombineAnimationEnd: () => void;
}

export interface UseConnectionReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  sendMove: (action: GameAction) => void;
  requestSync: (requesterId: number) => void;
  sendGameReset: () => void;
}

export function useConnection({
  roomId,
  enabled,
  gameStateHook,
  onGameStart,
  onError,
  onCombineAnimation,
  onCombineAnimationEnd,
}: UseConnectionOptions): UseConnectionReturn {
  const { oderId } = useDiscord();
  const {
    initGame,
    loadState,
    executeAction,
    setPlayerId,
    setIsHost,
    setSpectator,
    getEngine,
  } = gameStateHook;

  const isSpectatorRef = useRef(false);
  const spectatorIdRef = useRef<number | null>(null);
  const playerIdRef = useRef<number | null>(null);
  const hasSyncedRef = useRef(false);
  const gameStartedRef = useRef(false);
  const lobbyReadySentRef = useRef(false);

  const handleMessage = useCallback((message: WSMessage) => {
    console.log(`[useConnection] Received: ${message.type}`, message.payload);

    switch (message.type) {
      case 'your_player_id': {
        const { playerId: myPlayerId, reconnected, totalPlayers } = message.payload;

        setPlayerId(myPlayerId);
        setIsHost(myPlayerId === 1); // Для совместимости, но не влияет на логику
        isSpectatorRef.current = false;
        playerIdRef.current = myPlayerId;

        // Seed всегда из roomId - детерминированный
        const seed = seedFromRoomId(roomId);

        if (reconnected && totalPlayers === 2) {
          // Переподключение когда оба игрока есть - запрашиваем sync
          sendMessage({
            type: 'sync_request',
            payload: { requesterId: myPlayerId },
          });
        } else if (totalPlayers === 1) {
          // Первый игрок - создаём игру и ждём второго
          initGame(seed, roomId);
        } else if (totalPlayers === 2 && !reconnected) {
          // Второй игрок подключился - создаём свой движок с тем же seed
          initGame(seed, roomId);
          // Игра начинается
          gameStartedRef.current = true;
          onGameStart();
        }
        break;
      }

      case 'your_spectator_id': {
        const { spectatorId: mySpectatorId } = message.payload;

        setSpectator(mySpectatorId);
        isSpectatorRef.current = true;
        spectatorIdRef.current = mySpectatorId;

        // Запрашиваем состояние у игроков
        sendMessage({
          type: 'sync_request',
          payload: { requesterId: mySpectatorId },
        });
        break;
      }

      case 'player_joined': {
        const { totalPlayers } = message.payload;

        // Когда подключается второй игрок - игра начинается
        if (totalPlayers === 2 && !gameStartedRef.current) {
          gameStartedRef.current = true;
          onGameStart();
        }
        break;
      }

      case 'move': {
        const moveMsg = message as MoveMessage;
        const { playerId: actionPlayerId, action } = moveMsg.payload;

        // Игнорируем свои ходы
        if (actionPlayerId === playerIdRef.current) {
          return;
        }

        if (action.type === 'combine') {
          onCombineAnimation([action.cardId1, action.cardId2]);
          setTimeout(() => {
            executeAction(actionPlayerId, action);
            onCombineAnimationEnd();
          }, 1000);
        } else {
          executeAction(actionPlayerId, action);
        }
        break;
      }

      case 'player_left': {
        onError('Second player disconnected');
        break;
      }

      case 'spectator_joined': {
        // Ничего не делаем
        break;
      }

      case 'sync_request': {
        const { requesterId } = message.payload;
        const engine = getEngine();

        // Любой игрок с движком может ответить
        if (engine && playerIdRef.current !== null && !isSpectatorRef.current) {
          const currentState = engine.getState();

          sendMessage({
            type: 'sync_response',
            payload: {
              targetId: requesterId,
              gameState: currentState,
              fromPlayerId: playerIdRef.current,
            },
          });
        }
        break;
      }

      case 'sync_response': {
        const { targetId, targetSpectatorId, gameState: receivedState } = message.payload;
        const responseTargetId = targetId || targetSpectatorId;

        const mySpectatorId = spectatorIdRef.current;
        const amISpectator = isSpectatorRef.current;
        const myPlayerId = playerIdRef.current;

        const isForMe = (amISpectator && mySpectatorId === responseTargetId) ||
                        (!amISpectator && myPlayerId === responseTargetId);

        if (isForMe && !hasSyncedRef.current) {
          hasSyncedRef.current = true;
          loadState(receivedState);
          gameStartedRef.current = true;
          onGameStart();
        }
        break;
      }

      case 'lobby_state': {
        // Server sends lobby_state on WebSocket connect
        // Auto-send lobby_ready if we haven't yet (player already went through lobby UI)
        console.log(`[useConnection] lobby_state received, lobbyReadySent: ${lobbyReadySentRef.current}`);
        if (!lobbyReadySentRef.current) {
          lobbyReadySentRef.current = true;
          console.log(`[useConnection] Sending auto lobby_ready`);
          sendMessage({
            type: 'lobby_ready',
            payload: { ready: true },
          });
        }
        break;
      }

      // Игнорируем старые типы сообщений (для обратной совместимости)
      case 'init_game':
      case 'game_state':
      case 'role_assigned':
      case 'lobby_participant_ready':
      case 'lobby_participant_joined':
      case 'game_start':
        break;
    }
  }, [
    roomId, initGame, loadState, executeAction,
    setPlayerId, setIsHost, setSpectator, getEngine, onGameStart, onError,
    onCombineAnimation, onCombineAnimationEnd
  ]);

  const { isConnected, isConnecting, error, sendMessage } = useWebSocket({
    roomId: roomId || 'temp',
    clientId: oderId,
    enabled: enabled && !!roomId,
    onMessage: handleMessage,
    onError: () => onError('Connection error'),
  });

  const sendMove = useCallback((action: GameAction) => {
    if (!playerIdRef.current) return;

    sendMessage({
      type: 'move',
      payload: { playerId: playerIdRef.current, action },
    });
  }, [sendMessage]);

  const requestSync = useCallback((requesterId: number) => {
    sendMessage({
      type: 'sync_request',
      payload: { requesterId },
    });
  }, [sendMessage]);

  const sendGameReset = useCallback(() => {
    sendMessage({
      type: 'game_reset',
      payload: {},
    });
  }, [sendMessage]);

  return {
    isConnected,
    isConnecting,
    connectionError: error,
    sendMove,
    requestSync,
    sendGameReset,
  };
}
